import {
  Dialog,
  Text,
  Layout,
  Tag,
  Field,
  TextField,
  MirandaChangeEvent,
  Banner,
  type WCTextField,
  ToggleGroup,
  Tooltip,
  Button,
  Icon,
} from '@loadsmart/miranda-react'
import { Modal } from 'antd'
import { DateHelper, DatePicker } from '@loadsmart/loadsmart-ui'
import { useCallback, useEffect, useState } from 'react'
import moment from 'moment'

import analytics, { AnalyticsEvent } from '../../util/analytics'
import type { GateFeedFilter } from '../../clients/type'
import { useMessage } from '../../hooks/useMessage'
import { useObservationExports } from '../../hooks/useObservationExports'

type ExportDialogProps = {
  isOpen: boolean
  handleClose: () => void
  filters: GateFeedFilter[]
  selectedLocation: {
    label: string
    value: string
    timezone: string
  }
}

export function ExportDialog({
  isOpen,
  handleClose,
  filters,
  selectedLocation,
}: ExportDialogProps) {
  const messenger = useMessage()

  const { exportData, data: csvData, isLoading: isExportLoading } = useObservationExports()

  const activeFilter = filters.filter(
    (filter) =>
      filter.type === 'property' ||
      filter.type === 'loadType' ||
      filter.type === 'chassisType' ||
      filter.type === 'isBare'
  )

  const timezone = selectedLocation.timezone ?? 'UTC'
  const currentDate = moment().tz(timezone)

  const [date, setDate] = useState<string>(currentDate.format())
  const [time, setTime] = useState<WCTextField['value']>(currentDate.format('HH:mm'))
  const [exportFormat, setExportFormat] = useState('assetExport')
  const [isBannerVisible, setIsBannerVisible] = useState(false)

  const handleDateChange = useCallback(({ target }) => {
    const value = moment(target.value).tz(timezone).format()
    setDate(value)
  }, [])

  const handleTimeChange = useCallback((e: MirandaChangeEvent<WCTextField>) => {
    setTime(e.target.value)
  }, [])

  const handleExportFormatChange = useCallback((e) => {
    setExportFormat(e.target.value)
  }, [])

  const handleExportData = useCallback(() => {
    const exportDate = new Date(date)
    exportDate.setHours(Number(time.split(':')[0]))
    exportDate.setMinutes(Number(time.split(':')[1]))

    try {
      exportData({
        filters,
        report: true,
        inventoryOnly: true,
        exportDate: moment(exportDate).format(),
        exportFormat,
        timezone,
      })

      analytics.track(AnalyticsEvent.ReportsExportData)
    } catch (e) {
      if (e instanceof Error) {
        // eslint-disable-next-line no-console
        console.error(`ERROR handleDataExport: ${e.message}`)
      }
      messenger.error(new Error('Something went wrong, could not export data'))
    } finally {
      return Promise.resolve()
    }
  }, [filters, date, time, exportFormat])

  useEffect(() => {
    if (csvData === '' && !isExportLoading) {
      setIsBannerVisible(true)
    }

    if (isExportLoading) {
      setIsBannerVisible(false)
    }
  }, [csvData, isExportLoading])

  return (
    <Modal open={isOpen} onCancel={handleClose} footer={null} centered width={400}>
      <Layout.Stack gap="spacing-6">
        <Layout.Stack>
          <Text variant="heading-md-bold">Export data</Text>
        </Layout.Stack>

        <Layout.Stack>
          <Layout.Stack gap="spacing-5">
            {!!activeFilter.length && (
              <Layout.Group align="center">
                <Text color="color-text-tertiary">Filter applied</Text>
                <Tag>{activeFilter[0].label}</Tag>
              </Layout.Group>
            )}

            <Layout.Stack gap="spacing-2">
              <Text>Format</Text>
              <Layout.Group gap="spacing-2">
                <ToggleGroup
                  type="single-strict"
                  size="small"
                  options={[
                    {
                      label: 'Asset export',
                      value: 'assetExport',
                    },
                    {
                      label: 'Event export',
                      value: 'eventExport',
                    },
                  ]}
                  value={exportFormat}
                  onChange={handleExportFormatChange}
                />
                <Tooltip
                  placement="bottom-start"
                  trigger="click,hover"
                  message="Asset export will export an Excel file where each row is an asset. Event export will export a gate event per row."
                >
                  <Button variant="icon" size="small">
                    <Icon name="question" />
                  </Button>
                </Tooltip>
              </Layout.Group>
            </Layout.Stack>

            <Layout.Stack>
              <Text>Date and time of the Yard report</Text>
              <Layout.Stack gap="spacing-1">
                <Layout.Grid>
                  <Field>
                    <Field.Label>Date</Field.Label>
                    <DatePicker
                      name="ingateDate"
                      value={date}
                      onChange={handleDateChange}
                      constraints={[[DateHelper().add('day', 1).toString(), null]]}
                    />
                  </Field>
                  <Field>
                    <Field.Label>Time</Field.Label>
                    <TextField name="time" type="time" value={time} onChange={handleTimeChange} />
                  </Field>
                </Layout.Grid>
                <Text color="color-text-tertiary" variant="body-sm">
                  The export reflects the exact time you input in the field.
                </Text>
              </Layout.Stack>

              {isBannerVisible && (
                <Banner variant="warning" dismissible dismissed={!isBannerVisible}>
                  <Banner.Description>No assets in this date and time</Banner.Description>
                </Banner>
              )}
            </Layout.Stack>
          </Layout.Stack>
        </Layout.Stack>

        <Layout.Group justify="flex-end">
          <Dialog.ActionSecondary onClick={handleClose} disabled={false}>
            Cancel
          </Dialog.ActionSecondary>
          <Dialog.ActionPrimary
            variant="primary"
            onClick={handleExportData}
            disabled={false}
            loading={isExportLoading}
          >
            Export
          </Dialog.ActionPrimary>
        </Layout.Group>
      </Layout.Stack>
    </Modal>
  )
}
